import { PLAYER } from "../../common/constants.ts";
import type { UpdateEvents } from "../../common/types.ts";
import type {
	MpLeague,
	MpMember,
	MpPendingFiring,
	MpSession,
} from "../../common/multiplayerConfig.ts";
import { last } from "../../common/utils.ts";
import { team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import { budgetForCheckpoint, tradesUsed } from "../multiplayer/checkpoints.ts";
import { currentRoundBids, type FaBid } from "../multiplayer/fa.ts";
import {
	evaluateFirings,
	loadCompletedSeasons,
} from "../multiplayer/firing.ts";
import {
	getClient,
	getLeagueIdForLid,
	getLobbyState,
	getSession,
} from "../multiplayer/index.ts";
import { listOffers, type MpTradeOffer } from "../multiplayer/offers.ts";
import { listRecentTrades, type RecentTrade } from "../multiplayer/trades.ts";
import {
	listResignCandidates,
	type ResignCandidate,
} from "../multiplayer/resign.ts";
import { getLastSeq } from "../multiplayer/sync.ts";
import { rosterChecksum } from "../multiplayer/checksum.ts";

const updateMultiplayerLeague = async (
	inputs: unknown,
	updateEvents: UpdateEvents,
) => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("multiplayer") ||
		updateEvents.includes("playerMovement")
	) {
		const lid = g.get("lid");
		const leagueId =
			typeof lid === "number" ? await getLeagueIdForLid(lid) : undefined;

		if (!leagueId) {
			return {
				isMultiplayer: false as const,
			};
		}

		let session: MpSession | undefined;
		let league: MpLeague | undefined;
		let members: MpMember[] = [];
		let offers: MpTradeOffer[] = [];
		let recentTrades: RecentTrade[] = [];
		let budgetUsed = 0;
		let budgetTotal = 0;
		let mpError: string | undefined;

		try {
			session = await getSession();
			if (session) {
				const state = await getLobbyState(leagueId);
				league = state.league;
				members = state.members;
				offers = await listOffers(leagueId);
				// Commissioner-only: recent (this-checkpoint) trades that can be
				// undone, e.g. an accidental trade
				if (session.userId === league.commissioner && !league.sim_open) {
					recentTrades = await listRecentTrades(
						leagueId,
						league.checkpoint_seq,
					);
				}
			}
		} catch (error) {
			mpError = error.message;
		}

		const me = members.find((m) => m.user_id === session?.userId);
		const runnerId = league ? (league.sim_runner ?? league.commissioner) : null;
		const runner = members.find((m) => m.user_id === runnerId);

		if (league && me?.tid !== null && me?.tid !== undefined) {
			try {
				budgetUsed = await tradesUsed(league, me.tid);
				budgetTotal = budgetForCheckpoint(league, league.checkpoint);
			} catch {
				// Budget display is non-critical
			}
		}

		// Free agency auction data
		let faPool: {
			pid: number;
			name: string;
			ovr: number;
			age: number;
			asking: number;
		}[] = [];
		let myBids: FaBid[] = [];
		let payroll = 0;
		const salaryCap = league ? g.get("salaryCap") : 0;
		const minContract = league ? g.get("minContract") : 0;
		const maxContractLength = league ? g.get("maxContractLength") : 5;
		if (league?.fa_open && me?.tid !== null && me?.tid !== undefined) {
			try {
				const freeAgents = await idb.cache.players.indexGetAll(
					"playersByTid",
					PLAYER.FREE_AGENT,
				);
				faPool = freeAgents
					.sort((x, y) => y.value - x.value)
					.slice(0, 250)
					.map((p) => ({
						pid: p.pid,
						name: `${p.firstName} ${p.lastName}`,
						ovr: last(p.ratings)!.ovr,
						age: g.get("season") - p.born.year,
						asking: p.contract.amount,
					}));
				const bids = await currentRoundBids(leagueId, league.fa_round);
				myBids = bids.filter((b) => b.user_id === session?.userId);
				payroll = await team.getPayroll(me.tid);
			} catch (error) {
				mpError = mpError ?? error.message;
			}
		}

		// Playoffs: a summary of the current round's series for the shared
		// bracket panel, plus who's alive
		let playoffSeries: {
			homeName: string;
			homeSeed: number;
			homeWon: number;
			homeHuman: boolean;
			awayName: string;
			awaySeed: number;
			awayWon: number;
			awayHuman: boolean;
			numGamesToWin: number;
		}[] = [];
		if (league?.playoffs_active) {
			try {
				const ps = await idb.cache.playoffSeries.get(g.get("season"));
				const userTids = g.get("userTids");
				if (ps && ps.currentRound >= 0 && ps.series[ps.currentRound]) {
					const numGamesToWin = helpers.numGamesToWinSeries(
						g.get("numGamesPlayoffSeries", "current")[ps.currentRound],
					);
					const teamName = (tid: number) =>
						g.get("teamInfoCache")[tid]
							? `${g.get("teamInfoCache")[tid]!.region} ${g.get("teamInfoCache")[tid]!.name}`
							: `Team ${tid}`;
					playoffSeries = ps.series[ps.currentRound]!.filter((s) => s.away).map(
						(s) => ({
							homeName: teamName(s.home.tid),
							homeSeed: s.home.seed,
							homeWon: s.home.won,
							homeHuman: userTids.includes(s.home.tid),
							awayName: teamName(s.away!.tid),
							awaySeed: s.away!.seed,
							awayWon: s.away!.won,
							awayHuman: userTids.includes(s.away!.tid),
							numGamesToWin,
						}),
					);
				}
			} catch (error) {
				mpError = mpError ?? error.message;
			}
		}

		// Commissioner rules: pending firings (staged at the offseason) and
		// hot-seat warnings for GMs approaching a firing
		let pendingFirings: MpPendingFiring[] = [];
		let hotSeats: { tid: number; message: string }[] = [];
		if (league?.checkpoint === "offseason") {
			try {
				const { data } = await getClient()
					.from("pending_firings")
					.select()
					.eq("league_id", leagueId);
				pendingFirings = (data ?? []) as MpPendingFiring[];
				const completed = await loadCompletedSeasons();
				hotSeats = evaluateFirings(
					members as any,
					completed,
					league.settings,
				).hotSeats;
			} catch (error) {
				mpError = mpError ?? error.message;
			}
		}

		// Re-sign checkpoint: my own expiring players still available to re-sign
		let myResignCandidates: ResignCandidate[] = [];
		if (
			league?.checkpoint === "resign" &&
			!league.sim_open &&
			me?.tid !== null &&
			me?.tid !== undefined
		) {
			try {
				const all = await listResignCandidates(leagueId);
				myResignCandidates = all.filter((c) => c.tid === me.tid);
			} catch (error) {
				mpError = mpError ?? error.message;
			}
		}

		return {
			isMultiplayer: true as const,
			leagueId,
			gamePhase: g.get("phase"),
			session,
			league,
			members,
			offers,
			recentTrades,
			myTid: me?.tid ?? null,
			budgetUsed,
			budgetTotal,
			isRunner: session !== undefined && session.userId === runnerId,
			isCommissioner:
				session !== undefined && session.userId === league?.commissioner,
			runnerName: runner?.display_name ?? "the commissioner",
			faPool,
			myBids,
			payroll,
			salaryCap,
			minContract,
			maxContractLength,
			myResignCandidates,
			playoffSeries,
			amIAlive:
				me?.tid !== null &&
				me?.tid !== undefined &&
				(league?.playoff_alive ?? []).includes(me.tid),
			pendingFirings,
			hotSeats,
			lastSeq: await getLastSeq(leagueId),
			checksumInfo: await rosterChecksum(),
			mpError,
		};
	}
};

export default updateMultiplayerLeague;
